import React, {
    Dispatch,
    SetStateAction,
    useCallback,
    useRef,
    useState,
} from "react";
import EventEmitter from "eventemitter3";
import { ITerminalOptions, Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";

import "xterm/css/xterm.css";

import cssColors from "@formant/ui/style/colorExports.scss";

import { ITeleopSession } from "../../../teleop/ITeleopSession";
import { breakpoints } from "../../../utils/breakpoint";

const encoder =
    typeof TextEncoder !== "undefined"
        ? new TextEncoder()
        : {
              encode(str: string) {
                  const raw = unescape(encodeURIComponent(str));
                  const bytes = new Uint8Array(raw.length);
                  for (let i = 0; i < raw.length; i++) {
                      bytes[i] = raw.charCodeAt(i);
                  }
                  return bytes;
              },
          };

const fontSizes = {
    normal: 12,
    tiny: 8,
} as const;

const terminalTitle = `\u001b[35;1m\r
    █▀▀ █▀█ █▀█ █▀▄▀█ ▄▀█ █▄ █ ▀█▀ \r
    █▀  █▄█ █▀▄ █ ▀ █ █▀█ █ ▀█  █  \r
\r\n`;

export const defaultTerminalFont = "JetBrains Mono";

export const defaultTerminalOptions: ITerminalOptions = {
    fontFamily: defaultTerminalFont,
    fontSize: fontSizes.normal,
    theme: {
        background: cssColors.terminalBackground,
        black: cssColors.terminalBlack,
        blue: cssColors.terminalBlue,
        brightBlack: cssColors.terminalBrightBlack,
        brightBlue: cssColors.terminalBrightBlue,
        brightCyan: cssColors.terminalBrightCyan,
        brightGreen: cssColors.terminalBrightGreen,
        brightMagenta: cssColors.terminalBrightMagenta,
        brightRed: cssColors.terminalBrightRed,
        brightWhite: cssColors.terminalBrightWhite,
        brightYellow: cssColors.terminalBrightYellow,
        cursor: cssColors.terminalCursor,
        cursorAccent: cssColors.terminalCursorAccent,
        cyan: cssColors.terminalCyan,
        foreground: cssColors.terminalForeground,
        green: cssColors.terminalGreen,
        magenta: cssColors.terminalMagenta,
        red: cssColors.terminalRed,
        selectionBackground: cssColors.terminalSelection,
        white: cssColors.terminalWhite,
        yellow: cssColors.terminalYellow,
    },
    allowTransparency: true,
};

function establishConnection(
    bus: EventEmitter,
    session: ITeleopSession,
    terminalRef: React.MutableRefObject<Terminal | null>,
    startMessage: string,
    setTerminalReady: Dispatch<SetStateAction<boolean>>,
) {
    if (session.signalingVersion() !== 2) {
        console.error(
            "session.signalingVersion %s !== 2",
            session.signalingVersion(),
        );
        // This should only ever be working on RTC Version 2
        terminalRef.current?.write(
            "THIS AGENT MUST BE UPGRADED TO START A REMOTE TERMINAL…\n\r",
        );
        return () => {};
    }

    terminalRef.current?.writeln("CREATING TERMINAL…");

    const { devicePeer, rtcClient } = session;

    if (!rtcClient || !devicePeer) {
        terminalRef.current?.writeln(
            "RTC client or device peer were non-existent when terminal was created…",
        );
        throw Error(
            "RTC client or device peer were non-existent when terminal was created",
        );
    }

    if (!session.isRtcV2(rtcClient)) {
        console.error(
            "rtcClient has no method `createCustomDataChannel`. Incorrect signaling version?",
        );
        terminalRef.current?.write(
            "Unable to establish custom data channel…\n\r",
        );
        return () => {};
    }

    let disposed = false;
    const teardown = rtcClient.createCustomDataChannel(
        devicePeer.id,
        "terminal",
        { ordered: true },
        true,
        (_: string, dataChannel: RTCDataChannel) => {
            if (disposed) {
                return;
            }

            dataChannel.onopen = () => {
                terminalRef.current?.write("\x1bc");
                terminalRef.current?.write(terminalTitle);
                terminalRef.current?.write(startMessage);
                terminalRef.current?.focus();
            };

            dataChannel.onclose = () => {
                setTerminalReady(false);
                terminalRef.current?.write("\n\rTERMINAL SESSION HAS ENDED");
            };

            dataChannel.onmessage = evt => {
                terminalRef.current?.write(new Uint8Array(evt.data));
            };

            dataChannel.onerror = (err: any) => {
                setTerminalReady(false);
                terminalRef.current?.write(
                    "ERROR: LOST CONNECTION WITH AGENT\n\r" + err.error.message,
                );
            };

            terminalRef.current?.onKey(({ key }) => {
                if (dataChannel.readyState === "open") {
                    dataChannel.send(encoder.encode(key));
                }
            });

            bus.on("copy", async () => {
                try {
                    if (terminalRef.current) {
                        await navigator.clipboard.writeText(
                            terminalRef.current.getSelection(),
                        );
                    }
                } catch (error) {
                    console.error(error);
                    return;
                }
            });

            bus.on("paste", async () => {
                try {
                    const text = await navigator.clipboard.readText();
                    dataChannel.send(encoder.encode(text));
                } catch (error) {
                    console.error(error);
                    return;
                }
            });

            setTerminalReady(true);
        },
    );

    return () => {
        bus.removeAllListeners("paste");
        bus.removeAllListeners("copy");
        disposed = true;
        teardown();
        setTerminalReady(false);
    };
}

function initializeTerminal(
    el: HTMLDivElement,
    bus: EventEmitter,
    session: ITeleopSession,
    startMessage: string,
    setTerminalReady: Dispatch<SetStateAction<boolean>>,
    terminalRef: React.MutableRefObject<Terminal | null>,
    fitRef: React.MutableRefObject<FitAddon | null>,
    unregister: React.MutableRefObject<(() => void) | null>,
) {
    const terminal = new Terminal(defaultTerminalOptions);
    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    terminal.open(el);
    fitAddon.fit();
    terminal.scrollToBottom();

    terminalRef.current = terminal;
    fitRef.current = fitAddon;

    unregister.current = establishConnection(
        bus,
        session,
        terminalRef,
        startMessage,
        setTerminalReady,
    );
}

function teardownTerminal(
    terminalRef: React.MutableRefObject<Terminal | null>,
    fitRef: React.MutableRefObject<FitAddon | null>,
    unregister: React.MutableRefObject<(() => void) | null>,
) {
    unregister.current?.();
    unregister.current = null;

    fitRef.current?.dispose();
    fitRef.current = null;

    terminalRef.current?.dispose();
    terminalRef.current = null;
}

export function useTeleopTerminal(
    session: ITeleopSession,
    startMessage: string,
) {
    const [isReady, setIsReady] = useState(false);

    const [bus] = useState(() => new EventEmitter());

    const terminalRef = useRef<Terminal | null>(null);
    const fitRef = useRef<FitAddon | null>(null);
    const dataChannelUnregister = useRef<(() => void) | null>(null);

    const onCopy = useCallback(() => bus.emit("copy"), [bus]);
    const onPaste = useCallback(() => bus.emit("paste"), [bus]);

    const onResize = useCallback(
        ({ width }: DOMRectReadOnly) => {
            const terminal = terminalRef.current;
            if (terminal) {
                terminal.options.fontSize =
                    width < breakpoints.sm ? fontSizes.tiny : fontSizes.normal;
                fitRef.current?.fit();
                terminal.scrollToBottom();
            }
        },
        [terminalRef, fitRef],
    );

    const containerRef = useCallback(
        (el: HTMLDivElement | null) => {
            if (el !== null) {
                initializeTerminal(
                    el,
                    bus,
                    session,
                    startMessage,
                    setIsReady,
                    terminalRef,
                    fitRef,
                    dataChannelUnregister,
                );
            } else {
                teardownTerminal(terminalRef, fitRef, dataChannelUnregister);
            }
        },
        [
            terminalRef,
            fitRef,
            dataChannelUnregister,
            session,
            startMessage,
            bus,
            setIsReady,
        ],
    );

    return {
        isReady,
        containerRef,
        onResize,
        onCopy,
        onPaste,
    };
}
